import { useState, type DragEvent, type ReactNode } from 'react';
import { type TaskSummary } from '@todo/shared';
import { COLUMN_BATCH } from '../hooks/useColumnBoard';
import { CollapsibleColumn, type BoardColumn } from './CollapsibleColumn';

/** Re-exported so existing imports (hooks, pages) keep working - the type
 *  now lives with the CollapsibleColumn component. */
export type { BoardColumn } from './CollapsibleColumn';

/** My Day's 4 working columns - what "today's board" actually tracks.
 *  Cancelled is left out here for the same reason My Day leaves it out:
 *  it is not live work, so a page that wants it visible surfaces it
 *  separately, not as a 5th column. */
export const WORK_COLUMNS: BoardColumn[] = ['PENDING', 'ONGOING', 'BLOCKED', 'DONE'];

/** Every status as a column - for the one page (Tasks) whose own status
 *  filter can ask for Cancelled directly, and needs somewhere to put the
 *  answer. */
export const ALL_COLUMNS: BoardColumn[] = [
  'PENDING', 'ONGOING', 'BLOCKED', 'DONE', 'CANCELLED',
];

function CardSkeleton() {
  return (
    <>
      {[0, 1].map((i) => (
        <div key={i} className="h-20 animate-pulse rounded-lg border border-slate-200 bg-white" />
      ))}
    </>
  );
}

/**
 * The My Day board's column chrome, reused wherever a page wants that same
 * look. The page decides what belongs in the list and how each card
 * renders; this just lays the result out as a horizontally-scrolling set of
 * columns - and, when `onDropTask` is given, accepts a card dropped from a
 * `draggable` TaskCard the same way My Day's own board does.
 *
 * For a flat, already-fully-loaded task list (Tasks page, which has its own
 * global Prev/Next pagination already). For per-column "Load more" paging,
 * see `TaskBoardPaged` below.
 */
export function TaskBoard({
  tasks, loading, renderCard, columns = WORK_COLUMNS, emptyHint, onDropTask, doneCap,
}: {
  tasks: TaskSummary[];
  loading: boolean;
  /** `subtasks` is the task's own children found on this board - pass it to
   *  TaskCard to get the checklist-inside-the-card look. */
  renderCard: (task: TaskSummary, subtasks?: TaskSummary[]) => ReactNode;
  columns?: BoardColumn[];
  /** Per-column placeholder text when that column has no cards. */
  emptyHint?: (column: BoardColumn) => string;
  /** A `draggable` card was dropped on this column - move it there. Only
   *  PENDING/ONGOING/BLOCKED/DONE columns actually accept a drop, even on a
   *  board (like Tasks') that also shows Draft or Cancelled. */
  onDropTask?: (taskId: string, column: BoardColumn) => void;
  /** Cap the Completed column at this many MOST-RECENT cards; a "View
   *  more" row under them reveals the rest in cap-sized steps. The other
   *  columns are live work and always show everything. */
  doneCap?: number;
}) {
  const [doneVisible, setDoneVisible] = useState(doneCap ?? Number.POSITIVE_INFINITY);
  /**
   * Azure-Boards-style nesting: a sub-task whose PARENT is also on this
   * board renders INSIDE the parent's card (whatever column the parent
   * stands in), not as a card of its own. A sub-task whose parent is NOT
   * here - filtered out, or simply not yours to see - keeps its own card,
   * so work assigned to just you never disappears.
   */
  const { nested, childrenOf } = nestSubtasks(tasks);

  const byColumn = new Map<BoardColumn, TaskSummary[]>(columns.map((c) => [c, []]));
  for (const task of tasks) {
    if (nested.has(task.id)) continue;
    byColumn.get(task.status as BoardColumn)?.push(task);
  }

  function handleDrop(event: DragEvent, column: BoardColumn) {
    event.preventDefault();
    const id = event.dataTransfer.getData('text/plain');
    if (id) onDropTask?.(id, column);
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {columns.map((key) => {
        const full = byColumn.get(key) ?? [];
        // Completed is history, not live work: newest first, capped, with
        // the rest one "View more" click away.
        const capped = key === 'DONE' && doneCap !== undefined;
        const list = capped
          ? [...full]
              .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt))
              .slice(0, doneVisible)
          : full;
        const hidden = full.length - list.length;
        return (
          <CollapsibleColumn
            key={key}
            column={key}
            countLabel={full.length}
            onDrop={onDropTask ? handleDrop : undefined}
          >
            {loading ? (
              <CardSkeleton />
            ) : list.length === 0 ? (
              <p className="px-1 py-6 text-center text-xs text-slate-400">
                {emptyHint ? emptyHint(key) : 'Nothing here'}
              </p>
            ) : (
              <ul className="space-y-2">
                {list.map((task) => renderCard(task, childrenOf.get(task.id)))}
              </ul>
            )}
            {!loading && hidden > 0 && (
              <button
                type="button"
                onClick={() => setDoneVisible((v) => v + (doneCap ?? 10))}
                className="mt-2 w-full rounded-lg border border-dashed border-slate-300 bg-white px-2 py-2 text-center text-xs font-semibold text-brand-700 transition hover:bg-brand-50"
              >
                View {Math.min(doneCap ?? 10, hidden)} more
                <span className="mt-0.5 block text-[10.5px] font-medium text-slate-400">
                  {hidden} not shown
                </span>
              </button>
            )}
          </CollapsibleColumn>
        );
      })}
    </div>
  );
}

/**
 * Which tasks fold into a parent's card, and which parent holds them.
 * Shared by both board variants so nesting means the same thing everywhere.
 */
function nestSubtasks(tasks: TaskSummary[]): {
  /** Ids that must NOT render as their own card. */
  nested: Set<string>;
  /** parent id -> its sub-tasks present in this task set. */
  childrenOf: Map<string, TaskSummary[]>;
} {
  const ids = new Set(tasks.map((t) => t.id));
  const nested = new Set<string>();
  const childrenOf = new Map<string, TaskSummary[]>();
  for (const task of tasks) {
    if (task.parentTaskId && ids.has(task.parentTaskId)) {
      nested.add(task.id);
      const siblings = childrenOf.get(task.parentTaskId) ?? [];
      siblings.push(task);
      childrenOf.set(task.parentTaskId, siblings);
    }
  }
  return { nested, childrenOf };
}

/** One column's worth of state from `useColumnBoard` - kept structural
 *  (rather than importing the hook's own type) so this component only
 *  depends on the shape it actually needs. */
export interface PagedColumnState {
  items: TaskSummary[];
  total: number;
  loadingMore: boolean;
}

/**
 * Same column chrome as `TaskBoard`, but each column loads (and grows) on
 * its own: a "Load N more" row appears under a column's cards whenever the
 * server has more than what is currently loaded, independent of every other
 * column. Pair with `useColumnBoard`, which owns the per-column fetching.
 * Accepts a dropped card the same way `TaskBoard` does when `onDropTask` is
 * given.
 */
export function TaskBoardPaged({
  columns = WORK_COLUMNS,
  state,
  loading,
  onLoadMore,
  renderCard,
  emptyHint,
  onDropTask,
}: {
  columns?: BoardColumn[];
  state: Record<string, PagedColumnState>;
  loading: boolean;
  onLoadMore: (column: BoardColumn) => void;
  /** `subtasks` is the task's own children found anywhere on this board -
   *  pass it to TaskCard for the checklist-inside-the-card look. */
  renderCard: (task: TaskSummary, subtasks?: TaskSummary[]) => ReactNode;
  emptyHint?: (column: BoardColumn) => string;
  onDropTask?: (taskId: string, column: BoardColumn) => void;
}) {
  function handleDrop(event: DragEvent, column: BoardColumn) {
    event.preventDefault();
    const id = event.dataTransfer.getData('text/plain');
    if (id) onDropTask?.(id, column);
  }

  // Nesting looks across EVERY loaded column: the parent may stand in DONE
  // while its sub-task would land in PENDING - the sub-task still folds
  // into the parent's card rather than floating alone. (Column totals stay
  // the server's numbers; they count matching tasks, not visible cards.)
  const everyLoaded = columns.flatMap((key) => state[key]?.items ?? []);
  const { nested, childrenOf } = nestSubtasks(everyLoaded);

  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {columns.map((key) => {
        const col = state[key] ?? { items: [], total: 0, loadingMore: false };
        const remaining = Math.max(0, col.total - col.items.length);
        const cards = col.items.filter((task) => !nested.has(task.id));

        return (
          <CollapsibleColumn
            key={key}
            column={key}
            countLabel={loading ? '…' : `${col.items.length} of ${col.total}`}
            onDrop={onDropTask ? handleDrop : undefined}
          >
            {loading ? (
              <CardSkeleton />
            ) : cards.length === 0 ? (
              <p className="px-1 py-6 text-center text-xs text-slate-400">
                {col.items.length > 0
                  ? 'Shown inside their parent task'
                  : emptyHint ? emptyHint(key) : 'Nothing here'}
              </p>
            ) : (
              <ul className="space-y-2">
                {cards.map((task) => renderCard(task, childrenOf.get(task.id)))}
              </ul>
            )}

            {!loading && remaining > 0 && (
              <button
                type="button"
                disabled={col.loadingMore}
                onClick={() => onLoadMore(key)}
                className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-2 py-2 text-center text-xs font-semibold text-brand-700 transition hover:bg-brand-50 disabled:opacity-50"
              >
                {col.loadingMore ? 'Loading…' : `Load ${Math.min(COLUMN_BATCH, remaining)} more`}
                <span className="mt-0.5 block text-[10.5px] font-medium text-slate-400">
                  {remaining} remaining
                </span>
              </button>
            )}
          </CollapsibleColumn>
        );
      })}
    </div>
  );
}
